/**
 * Arlo & Co — Custom Photo Uploader
 * Replaces the Uploadcare widget with a custom grid UI.
 * Uses Uploadcare's Upload API for CDN hosting, Cropper.js for cropping.
 *
 * Reads config from #arlo-uploader data attributes (set by Liquid).
 * Stores a Uploadcare group/file URL in the hidden input so cart works identically.
 *
 * Requires: Cropper.js loaded before this script.
 */

(function () {
  'use strict';

  // ── Step coordinator (shared with arlo-options.js and arlo-variant-buttons.js) ──
  if (!window._arloStepCoord) {
    window._arloStepCoord = {
      _badges: [],
      _scheduled: false,
      register: function (el) {
        this._badges.push(el);
        if (!this._scheduled) {
          this._scheduled = true;
          var self = this;
          setTimeout(function () {
            self._badges.sort(function (a, b) {
              return (a.compareDocumentPosition(b) & 4) ? -1 : 1;
            });
            self._badges.forEach(function (b, i) { b.textContent = i + 1; });
          }, 0);
        }
      }
    };
  }

  // ── State ────────────────────────────────────────────────────────────────────
  var cfg = {};
  var slots = [];        // [{ state:'empty'|'loading'|'done'|'error', uuid, cdnUrl, origFile }]
  var cropSlotIdx = null;
  var cropperInst = null;
  var _cropEnforcing = false; // re-entry guard for minimum size enforcement
  var cropBlobUrl = null;
  var bwActive = false;  // black & white toggle state
  var satActive = false; // saturation boost toggle state

  // Arch crop shape — flat-bottomed, round-topped "tombstone" outline.
  // Achieved with plain border-radius (elliptical corners) at a fixed aspect
  // ratio, same "CSS illusion" approach as circle — no real pixel masking.
  var ARCH_ASPECT = 0.81; // width / height — MUST match assets/custom.js and admin/public/index.html
  // Vertical radius = (ARCH_ASPECT / 2) * 100 = 40.5% — this makes the top corners a true
  // semicircle (not a squished ellipse) at the ARCH_ASPECT ratio. If ARCH_ASPECT ever
  // changes, recompute this the same way in every file that duplicates it (see below).
  var ARCH_RADIUS = '50% 50% 4px 4px / 40.5% 40.5% 4px 4px';

  function buildCropFilter() {
    var parts = [];
    if (bwActive) parts.push('grayscale(100%)');
    if (satActive) {
      // In B&W mode Vivid boosts contrast; in colour mode it boosts saturation
      parts.push(bwActive ? 'contrast(1.2)' : 'saturate(1.3)');
    }
    return parts.join(' ');
  }

  function applyLiveFilter() {
    var cropperContainer = document.querySelector('.cropper-container');
    if (cropperContainer) cropperContainer.style.filter = buildCropFilter();
  }

  // ── CSS ──────────────────────────────────────────────────────────────────────
  var CSS = `
    .arlo-uploader { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin-bottom: 24px; }

    /* ── Step card header ── */
    .arlo-uploader__step-header {
      display: flex; align-items: center; gap: 12px;
      margin-bottom: 12px;
    }
    .arlo-uploader__step-badge {
      width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
      background: var(--color-button-primary-background, #111);
      color: var(--color-button-primary-text, #fff);
      font-size: 13px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-uploader__step-info { display: flex; flex-direction: column; gap: 1px; }
    .arlo-uploader__step-title {
      font-size: 12px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: var(--color-foreground, #111);
    }
    .arlo-uploader__step-sub {
      font-size: 11px; color: var(--color-foreground-secondary, #888);
    }

    .arlo-uploader__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
      gap: 8px;
    }
    .arlo-uploader--single .arlo-uploader__grid {
      grid-template-columns: 1fr;
      max-width: 320px;
      margin-left: auto;
      margin-right: auto;
    }

    /* ── Slot base ── */
    .arlo-slot {
      aspect-ratio: var(--arlo-slot-ratio, 1);
      border-radius: 8px;
      overflow: hidden;
      position: relative;
      cursor: pointer;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    /* ── Circle crop mode ── */
    .arlo-uploader--circle .arlo-slot { border-radius: 50%; }
    /* ── Arch crop mode ── */
    .arlo-uploader--arch .arlo-slot { border-radius: ${ARCH_RADIUS}; }

    /* ── Empty slot ── */
    .arlo-slot--empty {
      border: 2px dashed #d0ccc8;
      background: #faf9f7;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-slot--empty:hover { border-color: #888; background: #f0eeeb; }
    .arlo-slot__empty-inner {
      display: flex; flex-direction: column; align-items: center; gap: 4px;
      color: #999; pointer-events: none; padding: 8px; text-align: center;
    }
    .arlo-slot__empty-inner svg { width: 22px; height: 22px; opacity: 0.5; flex-shrink: 0; }
    .arlo-slot__empty-label {
      font-size: 10px; font-weight: 600; letter-spacing: 0.04em;
      text-transform: uppercase; color: var(--color-foreground, #111);
      line-height: 1.2;
    }
    .arlo-slot__empty-hint {
      font-size: 9px; color: #aaa; letter-spacing: 0.02em; line-height: 1.2;
    }
    /* In circle mode the inscribed area is narrower — add extra side padding */
    .arlo-uploader--circle .arlo-slot__empty-inner { padding: 20px 14px; }
    /* In arch mode the rounded top narrows the inscribed area up top */
    .arlo-uploader--arch .arlo-slot__empty-inner { padding: 28px 14px 12px; }

    /* ── Loading slot ── */
    .arlo-slot--loading {
      border: 2px solid #e0deda;
      background: #faf9f7;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-slot__upload-overlay {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: rgba(255,255,255,0.45);
    }
    .arlo-slot__spinner {
      width: 24px; height: 24px; border-radius: 50%;
      border: 3px solid #e0deda;
      border-top-color: #444;
      animation: arlo-spin 0.7s linear infinite;
    }
    @keyframes arlo-spin { to { transform: rotate(360deg); } }

    /* ── Done slot ── */
    .arlo-slot--done {
      border: 2px solid var(--color-button-primary-background, #111);
      background: #e8e8e8;
    }
    .arlo-slot--done:hover .arlo-slot__actions { opacity: 1; }
    .arlo-slot__thumb {
      position: absolute; inset: 0;
      width: 100%; height: 100%; object-fit: cover; display: block;
      transition: opacity 0.15s;
    }
    .arlo-slot--done:hover .arlo-slot__thumb { opacity: 0.6; }
    .arlo-slot__actions {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      opacity: 0; transition: opacity 0.15s;
    }
    .arlo-slot__btn {
      width: 32px; height: 32px; border-radius: 50%;
      border: none; cursor: pointer; font-size: 14px;
      display: flex; align-items: center; justify-content: center;
      backdrop-filter: blur(4px);
    }
    .arlo-slot__btn--remove { background: rgba(220,53,69,0.9); color: #fff; }
    .arlo-slot__btn--crop   { background: rgba(255,255,255,0.9); color: #222; }

    /* ── Error slot ── */
    .arlo-slot--error {
      border: 2px solid #dc3545;
      background: #fff5f5;
      display: flex; align-items: center; justify-content: center;
    }
    .arlo-slot__err-inner {
      font-size: 10px; color: #dc3545; text-align: center; padding: 4px;
    }

    /* ── Error message ── */
    .arlo-uploader__msg {
      margin-top: 10px; font-size: 12px; color: #dc3545;
      display: none;
    }

    /* ── Crop modal ── */
    .arlo-crop-overlay {
      position: fixed; inset: 0; z-index: 9999;
      display: flex; align-items: stretch;
      background: #fff; /* mobile: white full-screen */
    }
    .arlo-crop-card {
      display: flex; flex-direction: column;
      width: 100%; background: #fff;
    }
    .arlo-crop-topbar {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 16px; flex-shrink: 0;
      background: #fff; border-bottom: 1px solid #e5e5e5;
    }
    .arlo-crop-topbar-title {
      color: #111; font-size: 15px; font-weight: 600; letter-spacing: 0.02em;
      text-transform: uppercase;
    }
    .arlo-crop-close {
      background: none; border: 1px solid #ccc; cursor: pointer;
      padding: 4px 10px; color: #444; font-size: 11px; font-weight: 600;
      letter-spacing: 0.06em; text-transform: uppercase;
    }
    .arlo-crop-close:hover { background: #f0f0f0; }
    .arlo-crop-body {
      flex: 1; overflow: hidden; position: relative; min-height: 0;
      background: #fff;
    }
    .arlo-crop-body img { display: block; max-width: 100%; max-height: 100%; }
    .arlo-crop-body .cropper-modal { opacity: 0.65 !important; }
    .cropper-crop-box--circle .cropper-view-box,
    .cropper-crop-box--circle .cropper-face { border-radius: 50% !important; }
    .cropper-crop-box--arch .cropper-view-box,
    .cropper-crop-box--arch .cropper-face { border-radius: ${ARCH_RADIUS} !important; }
    .arlo-crop-footer {
      background: #fff; padding: 12px 16px;
      padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
      border-top: 1px solid #e5e5e5;
      display: flex; gap: 10px; flex-shrink: 0;
    }
    .arlo-crop-btn {
      flex: 1; padding: 13px 16px; border-radius: 0; border: none;
      font-size: 13px; font-weight: 600; cursor: pointer;
      letter-spacing: 0.08em; text-transform: uppercase;
      transition: opacity 0.15s;
    }
    .arlo-crop-btn:hover { opacity: 0.85; }
    .arlo-crop-btn--cancel { background: #f0f0f0; color: #444; border: 1px solid #ccc; }
    .arlo-crop-btn--done   { background: var(--color-button-primary-background, #1c3a52); color: var(--color-button-primary-text, #fff); }
    .arlo-crop-btn--rotate { flex: 0 0 48px; background: #f0f0f0; color: #444; border: 1px solid #ccc; font-size: 18px; padding: 13px 0; }
    .arlo-crop-btn--bw { flex: 0 0 auto; background: #f0f0f0; color: #444; border: 1px solid #ccc; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; padding: 13px 10px; }
    .arlo-crop-btn--bw.active { background: #222; color: #fff; border-color: #222; }
    /* ── Mobile landscape: full-screen, topbar hidden, tools on RHS ── */
    @media (max-width: 767px) and (orientation: landscape) {
      .arlo-crop-card {
        position: relative;
        display: grid;
        grid-template-areas: "body footer";
        grid-template-columns: 1fr 72px;
        grid-template-rows: 1fr;
        height: 100%;
      }
      .arlo-crop-topbar { display: none; }
      .arlo-crop-body {
        grid-area: body;
        height: 100%;
      }
      .arlo-crop-footer {
        grid-area: footer;
        flex-direction: column;
        border-top: none;
        border-left: 1px solid #e5e5e5;
        padding: 48px 6px 10px;
        padding-right: calc(6px + env(safe-area-inset-right, 0px));
        padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
        gap: 8px;
        overflow-y: auto;
      }
      .arlo-crop-btn {
        padding: 10px 4px;
        font-size: 10px;
        flex: 0 0 auto;
      }
      .arlo-crop-btn--done {
        white-space: normal;
        text-align: center;
        line-height: 1.3;
        flex: 1;
        min-height: 48px;
      }
      .arlo-crop-btn--rotate { font-size: 20px; padding: 10px 0; }
      .arlo-crop-landscape-close { display: flex !important; }
    }
    .arlo-crop-landscape-close {
      display: none;
      position: absolute;
      top: 8px; left: 8px; z-index: 10;
      width: 32px; height: 32px;
      align-items: center; justify-content: center;
      background: rgba(0,0,0,0.45); border: none; border-radius: 50%;
      color: #fff; font-size: 18px; cursor: pointer; line-height: 1;
    }
    /* ── Desktop: centred card over dark backdrop ── */
    @media (min-width: 768px) {
      .arlo-crop-overlay {
        background: rgba(0,0,0,0.72);
        align-items: center; justify-content: center;
        padding: 24px;
      }
      .arlo-crop-card {
        width: 100%; max-width: 860px;
        max-height: 90vh;
        box-shadow: 0 24px 64px rgba(0,0,0,0.35);
      }
      .arlo-crop-body { max-height: calc(90vh - 110px); }
    }
  `;

  function injectStyles() {
    if (document.getElementById('arlo-uploader-styles')) return;
    var s = document.createElement('style');
    s.id = 'arlo-uploader-styles';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  function mount(root) {
    injectStyles();
    root.innerHTML = '';
    root.className = 'arlo-uploader'
      + (cfg.circle ? ' arlo-uploader--circle' : '')
      + (cfg.shape === 'arch' ? ' arlo-uploader--arch' : '')
      + (cfg.maxImages === 1 ? ' arlo-uploader--single' : '');

    // Set slot aspect ratio from crop config (e.g. "3:2" → 1.5, "1181x591" → 2, circle → 1, arch → ARCH_ASPECT)
    var slotRatio = cfg.circle ? 1 : (cfg.shape === 'arch' ? ARCH_ASPECT : (parseCropRatio(cfg.crop) || 1));
    root.style.setProperty('--arlo-slot-ratio', slotRatio);

    // Step card header — badge number assigned by coordinator
    var stepHeader = document.createElement('div');
    stepHeader.className = 'arlo-uploader__step-header';
    var uploaderBadge = document.createElement('div');
    uploaderBadge.className = 'arlo-uploader__step-badge';
    uploaderBadge.textContent = '';
    if (window._arloStepCoord) window._arloStepCoord.register(uploaderBadge);
    var uploaderInfo = document.createElement('div');
    uploaderInfo.className = 'arlo-uploader__step-info';
    uploaderInfo.innerHTML =
        '<div class="arlo-uploader__step-title">Upload your photo' + (cfg.maxImages > 1 ? 's' : '') + '</div>'
      + '<div class="arlo-uploader__step-sub" id="arlo-count">Add ' + (cfg.maxImages === 1 ? 'your photo' : cfg.maxImages + ' photos') + ' to continue</div>';
    stepHeader.appendChild(uploaderBadge);
    stepHeader.appendChild(uploaderInfo);
    root.appendChild(stepHeader);

    // Grid
    var grid = document.createElement('div');
    grid.className = 'arlo-uploader__grid';
    grid.id = 'arlo-grid';
    root.appendChild(grid);

    // Error msg
    var msg = document.createElement('div');
    msg.className = 'arlo-uploader__msg';
    msg.id = 'arlo-msg';
    root.appendChild(msg);

    // Hidden file input
    var fi = document.createElement('input');
    fi.type = 'file';
    fi.accept = 'image/*';
    fi.style.display = 'none';
    fi.id = 'arlo-file-input';
    fi.addEventListener('change', onFileChosen);
    root.appendChild(fi);

    // Crop modal (hidden initially)
    var modal = buildCropModal();
    modal.id = 'arlo-crop-modal';
    modal.style.display = 'none';
    document.body.appendChild(modal);

    renderGrid();
    updateCount();
  }

  function renderGrid() {
    var grid = document.getElementById('arlo-grid');
    if (!grid) return;
    grid.innerHTML = '';
    slots.forEach(function (slot, i) {
      grid.appendChild(makeSlotEl(slot, i));
    });
  }

  function makeSlotEl(slot, i) {
    var el = document.createElement('div');
    el.className = 'arlo-slot arlo-slot--' + slot.state;
    el.dataset.idx = i;

    if (slot.state === 'empty') {
      el.innerHTML = '<div class="arlo-slot__empty-inner">'
        + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">'
        + '<path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>'
        + '<circle cx="12" cy="13" r="4"/></svg>'
        + '<span class="arlo-slot__empty-label">Tap to upload</span>'
        + '<span class="arlo-slot__empty-hint">JPG · PNG · HEIC</span>'
        + '</div>';
      el.addEventListener('click', function () { triggerPicker(i); });

    } else if (slot.state === 'loading') {
      if (slot.previewUrl) {
        // Show cropped preview immediately while uploading — spinner overlaid on top
        el.innerHTML = '<img class="arlo-slot__thumb" src="' + slot.previewUrl + '" alt="">'
          + '<div class="arlo-slot__upload-overlay"><div class="arlo-slot__spinner"></div></div>';
      } else {
        el.innerHTML = '<div class="arlo-slot__spinner"></div>';
      }

    } else if (slot.state === 'done') {
      el.innerHTML = '<img class="arlo-slot__thumb" src="' + slot.cdnUrl + '" alt="Photo ' + (i + 1) + '">'
        + '<div class="arlo-slot__actions">'
        + '<button type="button" class="arlo-slot__btn arlo-slot__btn--crop" title="Re-crop">✎</button>'
        + '<button type="button" class="arlo-slot__btn arlo-slot__btn--remove" title="Remove">×</button>'
        + '</div>';
      el.querySelector('.arlo-slot__btn--remove').addEventListener('click', function (e) { e.stopPropagation(); clearSlot(i); });
      el.querySelector('.arlo-slot__btn--crop').addEventListener('click', function (e)   { e.stopPropagation(); openCropForSlot(i, null); });

    } else if (slot.state === 'error') {
      el.innerHTML = '<div class="arlo-slot__err-inner">Upload failed<br><small>Tap to retry</small></div>';
      el.addEventListener('click', function () { triggerPicker(i); });
    }

    return el;
  }

  function refreshSlot(i) {
    var grid = document.getElementById('arlo-grid');
    if (!grid) return;
    var old = grid.querySelector('[data-idx="' + i + '"]');
    if (old) grid.replaceChild(makeSlotEl(slots[i], i), old);
    updateCount();
  }

  function updateCount() {
    var el = document.getElementById('arlo-count');
    if (!el) return;
    var done = slots.filter(function (s) { return s.state === 'done'; }).length;
    var total = slots.length;
    if (done === 0) {
      el.textContent = total === 1 ? 'Add your photo to continue' : 'Add ' + total + ' photos to continue';
    } else if (done < total) {
      el.textContent = done + ' of ' + total + ' photos uploaded';
    } else {
      el.textContent = total === 1 ? '✓ Photo uploaded' : '✓ All ' + total + ' photos uploaded';
    }
    updateStickyBtn(done, total);
  }

  // ── Sticky ATC button label ───────────────────────────────────────────────────
  // Relabels the [data-add-to-cart-text] span inside ATC buttons to reflect
  // upload progress. Targets the inner span so the button's DOM structure
  // (which the theme JS depends on) stays intact.
  var _stickyOrigText = null;

  function updateStickyBtn(done, total) {
    var btns = document.querySelectorAll('[data-add-to-cart]');
    if (!btns.length) return;

    var ready = (total - done) === 0;

    // Dim/restore the button — text stays as-is
    btns.forEach(function (btn) {
      btn.style.opacity = ready ? '' : '0.6';
      btn.style.cursor  = ready ? '' : 'default';
    });
  }

  // ── File picker ──────────────────────────────────────────────────────────────
  function triggerPicker(slotIdx) {
    cropSlotIdx = slotIdx;
    var fi = document.getElementById('arlo-file-input');
    if (fi) { fi.value = ''; fi.click(); }
  }

  function onFileChosen(e) {
    var file = e.target.files[0];
    if (!file || cropSlotIdx === null) return;
    openCropForSlot(cropSlotIdx, file);
  }

  // ── Crop modal ───────────────────────────────────────────────────────────────
  function buildCropModal() {
    // Overlay = full-screen backdrop (dark on desktop, white on mobile)
    var overlay = document.createElement('div');
    overlay.className = 'arlo-crop-overlay';

    // Card = the white box centred inside the overlay on desktop
    var card = document.createElement('div');
    card.className = 'arlo-crop-card';

    // Top bar with title + close button
    var topbar = document.createElement('div');
    topbar.className = 'arlo-crop-topbar';
    var title = document.createElement('span');
    title.className = 'arlo-crop-topbar-title';
    title.textContent = 'Crop your photo';
    var closeBtn = document.createElement('button');
    closeBtn.className = 'arlo-crop-close';
    closeBtn.textContent = 'Close';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', closeCropModal);
    topbar.appendChild(title);
    topbar.appendChild(closeBtn);
    card.appendChild(topbar);

    var body = document.createElement('div');
    body.className = 'arlo-crop-body';
    var img = document.createElement('img');
    img.id = 'arlo-crop-img';
    body.appendChild(img);
    card.appendChild(body);

    var modalMsg = document.createElement('div');
    modalMsg.id = 'arlo-crop-msg';
    modalMsg.style.cssText = 'display:none;color:#c0392b;font-size:12px;text-align:center;padding:6px 16px 0;font-family:-apple-system,sans-serif';
    card.appendChild(modalMsg);

    var footer = document.createElement('div');
    footer.className = 'arlo-crop-footer';

    var rotL = document.createElement('button');
    rotL.type = 'button';
    rotL.className = 'arlo-crop-btn arlo-crop-btn--rotate';
    rotL.title = 'Rotate left';
    rotL.innerHTML = '↺';
    rotL.addEventListener('click', function () { if (cropperInst) cropperInst.rotate(-90); });

    var rotR = document.createElement('button');
    rotR.type = 'button';
    rotR.className = 'arlo-crop-btn arlo-crop-btn--rotate';
    rotR.title = 'Rotate right';
    rotR.innerHTML = '↻';
    rotR.addEventListener('click', function () { if (cropperInst) cropperInst.rotate(90); });

    var done = document.createElement('button');
    done.type = 'button'; // must be explicit — default 'submit' would submit the product form
    done.className = 'arlo-crop-btn arlo-crop-btn--done';
    done.textContent = '✓ Use this photo';
    done.addEventListener('click', onCropDone);

    var bwBtn = document.createElement('button');
    bwBtn.type = 'button';
    bwBtn.className = 'arlo-crop-btn arlo-crop-btn--bw';
    bwBtn.id = 'arlo-bw-btn';
    bwBtn.title = 'Convert to black & white';
    bwBtn.textContent = 'B&W';
    bwBtn.addEventListener('click', function () {
      bwActive = !bwActive;
      bwBtn.classList.toggle('active', bwActive);
      applyLiveFilter();
    });

    var satBtn = document.createElement('button');
    satBtn.type = 'button';
    satBtn.className = 'arlo-crop-btn arlo-crop-btn--bw';
    satBtn.id = 'arlo-sat-btn';
    satBtn.title = 'Boost colour saturation';
    satBtn.textContent = 'Vivid';
    satBtn.addEventListener('click', function () {
      satActive = !satActive;
      satBtn.classList.toggle('active', satActive);
      applyLiveFilter();
    });

    footer.appendChild(rotL);
    footer.appendChild(rotR);
    footer.appendChild(bwBtn);
    footer.appendChild(satBtn);
    footer.appendChild(done);
    card.appendChild(footer);

    // Landscape-only × close button (top-left, shown via CSS in landscape)
    var lsClose = document.createElement('button');
    lsClose.type = 'button';
    lsClose.className = 'arlo-crop-landscape-close';
    lsClose.setAttribute('aria-label', 'Close');
    lsClose.innerHTML = '&times;';
    lsClose.addEventListener('click', closeCropModal);
    card.appendChild(lsClose);

    overlay.appendChild(card);
    return overlay;
  }

  function openCropForSlot(slotIdx, file) {
    cropSlotIdx = slotIdx;
    var modal = document.getElementById('arlo-crop-modal');
    if (!modal) return;

    // Revoke old blob URL
    if (cropBlobUrl) { URL.revokeObjectURL(cropBlobUrl); cropBlobUrl = null; }

    // Source: new file, or the slot's original file blob, or fallback to CDN url (re-crop)
    var src;
    if (file) {
      cropBlobUrl = URL.createObjectURL(file);
      src = cropBlobUrl;
      slots[slotIdx].origFile = file;
    } else if (slots[slotIdx].origFile) {
      cropBlobUrl = URL.createObjectURL(slots[slotIdx].origFile);
      src = cropBlobUrl;
    } else if (slots[slotIdx].cdnUrl) {
      // Re-crop from CDN — use original (no transforms) via /-/original/
      src = slots[slotIdx].cdnUrl.replace(/\/-\/.*$/, '') + '/-/original/';
    } else {
      return;
    }

    var img = document.getElementById('arlo-crop-img');

    // Destroy old Cropper
    if (cropperInst) { cropperInst.destroy(); cropperInst = null; }

    modal.style.display = 'flex';

    // On orientation change: debounced destroy + reinit so layout settles first.
    // Only listen to orientationchange — resize fires too many times and stacks instances.
    var _orientTimeout;
    function onOrientationChange() {
      clearTimeout(_orientTimeout);
      _orientTimeout = setTimeout(function () {
        var img2 = document.getElementById('arlo-crop-img');
        if (!img2 || !img2.src) return;
        if (cropperInst) { cropperInst.destroy(); cropperInst = null; }
        setTimeout(function () {
          var ar2 = parseCropRatio(cfg.crop);
          cropperInst = new Cropper(img2, {
            aspectRatio: cfg.circle ? 1 : (cfg.shape === 'arch' ? ARCH_ASPECT : ar2),
            viewMode: 1,
            autoCropArea: 0.95,
            responsive: true,
            checkCrossOrigin: false,
            background: false,
            ready: function () {
              if (cfg.circle) {
                var box = cropperInst.cropper.querySelector('.cropper-crop-box');
                if (box) box.classList.add('cropper-crop-box--circle');
              } else if (cfg.shape === 'arch') {
                var box2 = cropperInst.cropper.querySelector('.cropper-crop-box');
                if (box2) box2.classList.add('cropper-crop-box--arch');
              }
            }
          });
        }, 100);
      }, 400);
    }
    window.addEventListener('orientationchange', onOrientationChange);
    modal._cleanupOrient = function () {
      window.removeEventListener('orientationchange', onOrientationChange);
      clearTimeout(_orientTimeout);
    };

    // Reset effect states on each open
    bwActive = false;
    satActive = false;
    var bwBtn = document.getElementById('arlo-bw-btn');
    if (bwBtn) bwBtn.classList.remove('active');
    var satBtn = document.getElementById('arlo-sat-btn');
    if (satBtn) satBtn.classList.remove('active');
    applyLiveFilter();

    // Parse crop ratio from config  e.g. "1181x1181" → aspectRatio 1
    var ar = parseCropRatio(cfg.crop);

    // Set onload BEFORE src to avoid race condition where image loads before handler is attached
    img.onload = function () {
      if (typeof Cropper === 'undefined') {
        console.warn('[ArloUploader] Cropper.js not loaded');
        return;
      }
      cropperInst = new Cropper(img, {
        aspectRatio: cfg.circle ? 1 : (cfg.shape === 'arch' ? ARCH_ASPECT : ar),
        viewMode: 1,
        autoCropArea: 0.95,
        responsive: true,
        checkCrossOrigin: false,
        background: false,
        ready: function() {
          if (cfg.circle) {
            var box = cropperInst.cropper.querySelector('.cropper-crop-box');
            if (box) box.classList.add('cropper-crop-box--circle');
          } else if (cfg.shape === 'arch') {
            var box2 = cropperInst.cropper.querySelector('.cropper-crop-box');
            if (box2) box2.classList.add('cropper-crop-box--arch');
          }
        },
        // No real-time minimum enforcement — it locks the crop box and prevents repositioning.
        // Minimum size is checked at submit time in onCropDone() instead.
      });
    };

    // Set src after onload is wired up
    img.src = src;
  }

  function closeCropModal() {
    var modal = document.getElementById('arlo-crop-modal');
    if (modal) {
      modal.style.display = 'none';
      if (modal._cleanupOrient) { modal._cleanupOrient(); modal._cleanupOrient = null; }
    }
    if (cropperInst) { cropperInst.destroy(); cropperInst = null; }
    if (cropBlobUrl) { URL.revokeObjectURL(cropBlobUrl); cropBlobUrl = null; }
  }

  function onCropDone() {
    console.log('[ArloUploader] onCropDone called, cropperInst=', cropperInst, 'cropSlotIdx=', cropSlotIdx);
    if (!cropperInst) {
      console.error('[ArloUploader] cropperInst is null — Cropper.js may not have initialised');
      showMsg('Error: crop tool not ready. Try closing and re-opening the crop window.');
      return;
    }

    // ── Minimum resolution guard (only if enforceMinPx is enabled for this product) ──
    if (cfg.enforceMinPx && cfg.minPx) {
      var minParts = cfg.minPx.split('x');
      var minW = parseInt(minParts[0]), minH = parseInt(minParts[1]);
      if (minW && minH) {
        var d = cropperInst.getData(true);
        console.log('[ArloUploader] crop data', d, 'min', minW, minH);
        if (d.width < minW || d.height < minH) {
          var mm = document.getElementById('arlo-crop-msg');
          if (mm) { mm.textContent = 'Your image is too small. Please choose a higher quality photo.'; mm.style.display = 'block'; }
          return;
        }
      }
    }

    var canvas = cropperInst.getCroppedCanvas({ maxWidth: 2000, maxHeight: 2000, fillColor: '#fff', imageSmoothingEnabled: true });
    console.log('[ArloUploader] canvas=', canvas);
    if (!canvas) {
      showMsg('Error: could not read crop area. Please try again.');
      return;
    }
    if (bwActive) {
      // ctx.filter is unsupported on older iOS Safari — apply grayscale via pixel manipulation instead
      var fxCtx = canvas.getContext('2d');
      var imageData = fxCtx.getImageData(0, 0, canvas.width, canvas.height);
      var data = imageData.data;
      for (var i = 0; i < data.length; i += 4) {
        var gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
        data[i] = data[i + 1] = data[i + 2] = gray;
      }
      fxCtx.putImageData(imageData, 0, 0);
    }
    canvas.toBlob(function (blob) {
      console.log('[ArloUploader] blob size=', blob && blob.size);
      if (!blob) { showMsg('Error: could not generate image. Please try again.'); return; }
      closeCropModal();
      uploadBlob(cropSlotIdx, blob);
    }, 'image/jpeg', 0.93);
  }

  // ── Upload ───────────────────────────────────────────────────────────────────
  function uploadBlob(slotIdx, blob) {
    var previewUrl = URL.createObjectURL(blob);
    slots[slotIdx] = { state: 'loading', uuid: null, cdnUrl: null, origFile: slots[slotIdx].origFile, previewUrl: previewUrl };
    refreshSlot(slotIdx);

    var fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', cfg.publicKey);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', blob, 'photo-' + (slotIdx + 1) + '.jpg');

    fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd })
      .then(function (r) {
        if (!r.ok) throw new Error('Uploadcare returned HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (!data.file) throw new Error(JSON.stringify(data));
        URL.revokeObjectURL(slots[slotIdx].previewUrl);
        slots[slotIdx].state = 'done';
        slots[slotIdx].uuid = data.file;
        slots[slotIdx].cdnUrl = 'https://ucarecdn.com/' + data.file + '/';
        slots[slotIdx].previewUrl = null;
        refreshSlot(slotIdx);
        updateHiddenInput();
        showMsg('');
      })
      .catch(function (err) {
        console.error('[ArloUploader] upload error', err);
        URL.revokeObjectURL(slots[slotIdx].previewUrl);
        slots[slotIdx].previewUrl = null;
        slots[slotIdx].state = 'error';
        refreshSlot(slotIdx);
        showMsg('Upload failed: ' + err.message + '. Please check your Uploadcare account is active.');
      });
  }

  function updateHiddenInput() {
    var done = slots.filter(function (s) { return s.state === 'done'; });
    var hidden = document.getElementById('arlo-upload-value');
    if (!hidden) return;
    hidden.disabled = false; // suppressNativeOptions() may have disabled this — re-enable so serialize() picks it up

    // Remove any previously injected per-photo inputs
    document.querySelectorAll('.arlo-photo-prop').forEach(function (el) { el.remove(); });

    if (!done.length) {
      hidden.value = '';
      hidden.name = 'properties[Custom image]';
      return;
    }

    if (done.length === 1) {
      // Single image — use the existing hidden input directly
      hidden.name = 'properties[Custom image]';
      hidden.value = 'https://ucarecdn.com/' + done[0].uuid + '/';
      // Shape flag for cart display. Scoped to this exact photo's UUID via
      // _arlo_shape_uuids — without this, custom.js would apply the shape to
      // EVERY ucarecdn.com URL on the line item, including an unrelated
      // canvas-png personalised-message PNG on the same product.
      if (cfg.circle || cfg.shape === 'arch') {
        var f1 = hidden.closest('form') || document.querySelector('form[action*="/cart/add"]');
        if (f1) {
          if (cfg.circle) {
            var ci1 = document.createElement('input');
            ci1.type = 'hidden'; ci1.className = 'arlo-photo-prop';
            ci1.name = 'properties[_arlo_circle]'; ci1.value = 'true';
            f1.appendChild(ci1);
          }
          if (cfg.shape === 'arch') {
            var ai1 = document.createElement('input');
            ai1.type = 'hidden'; ai1.className = 'arlo-photo-prop';
            ai1.name = 'properties[_arlo_shape]'; ai1.value = 'arch';
            f1.appendChild(ai1);
          }
          var su1 = document.createElement('input');
          su1.type = 'hidden'; su1.className = 'arlo-photo-prop';
          su1.name = 'properties[_arlo_shape_uuids]'; su1.value = done[0].uuid;
          f1.appendChild(su1);
        }
      }
      return;
    }

    // Multiple images:
    // 1. Write each photo as its own visible property (for cart display)
    // 2. Create an Uploadcare group and store it as _Photo Group (for fulfillment)
    hidden.name = '_arlo_unused';
    hidden.value = '';

    var form = hidden.closest('form') || document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // Individual display properties (synchronous)
    done.forEach(function (slot, i) {
      var inp = document.createElement('input');
      inp.type = 'hidden';
      inp.className = 'arlo-photo-prop';
      inp.name = 'properties[Photo ' + (i + 1) + ']';
      inp.value = 'https://ucarecdn.com/' + slot.uuid + '/';
      form.appendChild(inp);
    });

    // Shape flag (hidden from cart UI via _ prefix, used for display styling).
    // Scoped to these exact photo UUIDs via _arlo_shape_uuids — see single-image
    // branch above for why the scoping matters.
    if (cfg.circle) {
      var ciMulti = document.createElement('input');
      ciMulti.type = 'hidden'; ciMulti.className = 'arlo-photo-prop';
      ciMulti.name = 'properties[_arlo_circle]'; ciMulti.value = 'true';
      form.appendChild(ciMulti);
    }
    if (cfg.shape === 'arch') {
      var aiMulti = document.createElement('input');
      aiMulti.type = 'hidden'; aiMulti.className = 'arlo-photo-prop';
      aiMulti.name = 'properties[_arlo_shape]'; aiMulti.value = 'arch';
      form.appendChild(aiMulti);
    }
    if (cfg.circle || cfg.shape === 'arch') {
      var suMulti = document.createElement('input');
      suMulti.type = 'hidden'; suMulti.className = 'arlo-photo-prop';
      suMulti.name = 'properties[_arlo_shape_uuids]';
      suMulti.value = done.map(function (s) { return s.uuid; }).join(',');
      form.appendChild(suMulti);
    }

    // Group URL property for fulfillment (async — hidden from cart UI via _ prefix)
    var groupInp = document.createElement('input');
    groupInp.type = 'hidden';
    groupInp.className = 'arlo-photo-prop';
    groupInp.id = 'arlo-group-url';
    groupInp.name = 'properties[_Photo Group]';
    groupInp.value = '';
    form.appendChild(groupInp);

    var uuids = done.map(function (s) { return s.uuid; });
    var params = new URLSearchParams();
    params.append('pub_key', cfg.publicKey);
    uuids.forEach(function (u, i) { params.append('files[' + i + ']', u + '/'); });

    fetch('https://upload.uploadcare.com/group/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.id) {
          groupInp.value = 'https://ucarecdn.com/' + data.id + '~' + uuids.length + '/';
        }
      })
      .catch(function (err) { console.error('[ArloUploader] group error', err); });
  }

  // ── Slot management ──────────────────────────────────────────────────────────
  function clearSlot(i) {
    slots[i] = { state: 'empty', uuid: null, cdnUrl: null, origFile: null };
    refreshSlot(i);
    updateHiddenInput();
  }

  function resetAll() {
    for (var i = 0; i < slots.length; i++) {
      slots[i] = { state: 'empty', uuid: null, cdnUrl: null, origFile: null };
      refreshSlot(i);
    }
    updateHiddenInput();
    updateStickyBtn(0, cfg.maxImages);
  }

  window.ArloPhotoUploader = { reset: resetAll };

  // ── Resize slots when variant changes (via arlo-variant-uploader.js) ─────────
  function resizeTo(n) {
    if (n === slots.length) return;
    if (n > slots.length) {
      while (slots.length < n) slots.push({ state: 'empty', uuid: null, cdnUrl: null, origFile: null });
    } else {
      slots = slots.slice(0, n);
    }
    cfg.maxImages = n;
    renderGrid();
    updateCount();
    updateHiddenInput();
  }

  // Watch #arlo-uploader for data-max-images attribute changes (set by variant uploader)
  function watchRoot(root) {
    var obs = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'data-max-images') {
          var n = parseInt(root.getAttribute('data-max-images')) || 1;
          resizeTo(n);
        }
        if (m.attributeName === 'data-crop') {
          cfg.crop = root.getAttribute('data-crop') || '';
        }
        if (m.attributeName === 'data-min-px') {
          cfg.minPx = root.getAttribute('data-min-px') || '';
        }
      });
    });
    obs.observe(root, { attributes: true, attributeFilter: ['data-max-images', 'data-crop', 'data-min-px'] });
  }

  // ── Cart validation ──────────────────────────────────────────────────────────
  // Prevent add-to-cart if not all slots are filled
  function hookCartForm() {
    function cartBlocked(e) {
      var done = slots.filter(function (s) { return s.state === 'done'; }).length;
      var needed = slots.length;
      if (done < needed) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showMsg(cfg.errorMessage || 'Please upload all ' + needed + ' photos before adding to cart.');
        var root = document.getElementById('arlo-uploader');
        if (root) root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }
      // For multi-image: block until the group URL is ready (async fetch)
      var groupInp = document.getElementById('arlo-group-url');
      if (groupInp && !groupInp.value) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showMsg('Almost ready — please try again in a moment.');
        return true;
      }
      return false;
    }

    // Main form submit (standard ATC button)
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('submit', cartBlocked, true);
    }

    // Sticky bar + any [data-add-to-cart] button — theme JS intercepts clicks
    // so we must also run in the capture phase here
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      cartBlocked(e);
    }, true);
  }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function parseCropRatio(crop) {
    if (!crop) return NaN;
    var s = String(crop).replace(' minimum', '');
    // "W:H" ratio format — e.g. "1:1", "4:3", "3:2"
    if (s.indexOf(':') !== -1) {
      var rp = s.split(':');
      var rw = parseFloat(rp[0]), rh = parseFloat(rp[1]);
      if (rw && rh) return rw / rh;
    }
    // "WxH" pixel format — legacy, e.g. "1181x1181"
    if (s.indexOf('x') !== -1) {
      var pp = s.split('x');
      var pw = parseFloat(pp[0]), ph = parseFloat(pp[1]);
      if (pw && ph) return pw / ph;
    }
    return NaN;
  }

  function showMsg(text) {
    var el = document.getElementById('arlo-msg');
    if (!el) return;
    el.textContent = text;
    el.style.display = text ? 'block' : 'none';
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    var root = document.getElementById('arlo-uploader');
    if (!root) return;

    cfg = {
      publicKey:    root.dataset.publicKey    || '',
      maxImages:    parseInt(root.dataset.maxImages) || 1,
      crop:         root.dataset.crop         || '',
      minPx:        root.dataset.minPx        || '',
      enforceMinPx: root.dataset.enforceMinPx === 'true',
      circle:       root.dataset.circle === 'true',
      shape:        root.dataset.shape        || (root.dataset.circle === 'true' ? 'circle' : ''),
      errorMessage: root.dataset.error        || ''
    };

    slots = Array.from({ length: cfg.maxImages }, function () {
      return { state: 'empty', uuid: null, cdnUrl: null, origFile: null };
    });

    mount(root);
    watchRoot(root);
    hookCartForm();
    updateStickyBtn(0, cfg.maxImages);
  }

  document.addEventListener('DOMContentLoaded', init);

})();
